import { contactPipelineInput } from 'app/lib/contactPipelineInput'
import { Disclosure } from '@headlessui/react'
import { Plus, Trash } from 'iconoir-react'
import { compact } from 'lodash'
import { observer } from 'mobx-react-lite'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { ChevronToggle } from 'app/components/ChevronToggle'
import type { PipelineCatalogEntry } from 'app/components/action-center/usePipelinesCatalog'
import { isSystemPipelineId } from 'app/components/action-center/systemPipelines'
import { AddPipeline } from 'app/components/contacts/AddPipeline'
import { ListMembershipRow } from 'app/components/contacts/ListMembershipRow'
import { PipelineStageDropdown } from 'app/components/contacts/PipelineStageDropdown'
import { RuleListRows } from 'app/components/contacts/RuleListRows'
import Spinner from 'app/components/Spinner'
import { Tooltip } from 'app/components/Tooltip'
import { Button } from 'app/components/ui/button'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useListCopy } from 'app/hooks/useListCopy'
import { useListPipeline } from 'app/hooks/useListPipeline'
import { usePracticeListMemberships } from 'app/hooks/usePracticeListMemberships'
import { useContactStore, usePipelineStore } from 'app/stores/StoreContext'
import type { ContactJson } from 'app/types/entities'
import type { StageType } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { notifyError } from 'app/services/notify.client'
import { useUser } from 'app/utils'
import { isCompanionPipeline, visibleMemberships } from 'lib/companionPipelines'
import { debug } from 'lib/logging'

interface ContactPipelinesV2Props {
  contact: ContactJson
  onContactChange?: (contact: ContactJson) => void
}

interface PipelineCardProps {
  pipelineId: string
  stageName: string
  contactId: string
  onChangeStage: (stageName: string) => void
  onRemove?: () => void
  /** A practice list from the List API: drawn as given, with no pipeline fetch. */
  entry?: PipelineCatalogEntry
}

function PipelineCard({
  pipelineId,
  stageName,
  contactId,
  onChangeStage,
  onRemove,
  entry,
}: PipelineCardProps): ReactElement {
  const copy = useListCopy()
  const { pipeline, error } = useListPipeline(pipelineId, !!entry)

  const stages = entry?.stages ?? pipeline?.stages
  const stage: StageType | undefined = useMemo(() => stages?.find((s) => s.name === stageName), [stages, stageName])
  const isLoading = !entry && !pipeline && !error

  debug([`contacts`, `pipelines`], pipelineId, stageName)
  debug([`contacts`, `pipelines`], `pipelines`, pipeline, `stage`, stage, `error`, error)

  if (error) {
    return (
      <div className="w-full rounded-lg border border-default bg-surface-subtle shadow-elevation-1">
        <div className="flex items-center overflow-clip rounded-[inherit] p-3">
          <div className="text-label-xs text-red-600">{copy.errorLoadingList}</div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="w-full rounded-lg border border-default bg-surface-subtle shadow-elevation-1">
        <div className="flex items-center overflow-clip rounded-[inherit] p-3">
          <Spinner size={4} className="mx-auto" />
        </div>
      </div>
    )
  }

  // The section filters companions out before it draws a card; this covers the moment before
  // the pipeline list has loaded, when the section cannot yet tell a companion apart.
  if (!entry && (!pipeline || !stage || isCompanionPipeline(pipeline))) {
    return <div />
  }

  const name = entry?.name ?? pipeline?.name ?? ``
  const to = entry
    ? `/lists/${entry.id}?drawer=${contactId}`
    : `/pipelines?tab=${pipeline?.id}&wId=${pipeline?.workspaceId}&drawer=${contactId}`

  return (
    <ListMembershipRow
      id={pipelineId}
      name={name}
      to={to}
      stage={<PipelineStageDropdown stages={stages ?? []} currentStage={stage?.name ?? ``} onChange={onChangeStage} />}
      actions={
        onRemove && (
          <Tooltip message={copy.removeFromList} placement="left">
            <Button variant="icon" aria-label={copy.removeFromList} onClick={onRemove} className="size-5">
              <Trash className="size-3.5 text-minimal" />
            </Button>
          </Tooltip>
        )
      }
    />
  )
}

export const ContactPipelinesV2 = observer(function ContactPipelinesV2({
  contact,
  onContactChange,
}: ContactPipelinesV2Props): ReactElement | null {
  const user = useUser()
  const contactStore = useContactStore()
  const pipelineStore = usePipelineStore()
  const copy = useListCopy()
  const groupedNav = useFeatureToggle(`grouped-navigation`)
  const practice = usePracticeListMemberships(contact, { enabled: !!groupedNav })
  const [optimisticPipelines, setOptimisticPipelines] = useState<ContactJson['pipelines']>()

  const pipelines = useMemo(() => {
    return optimisticPipelines ?? contact.pipelines ?? []
  }, [contact.pipelines, optimisticPipelines])

  useEffect(() => {
    if (!pipelineStore.list && !pipelineStore.listLoading) pipelineStore.fetchList()
  }, [pipelineStore])

  // Clear optimistic state when the prop catches up
  useEffect(() => {
    setOptimisticPipelines(undefined)
  }, [contact.pipelines])

  // Paint the pending shape locally only. The drawer's `onContactChange` refetches the
  // contact from the server, so calling it before the write lands reads back the old
  // pipelines and clears this optimistic state, which is the "needs a refresh" bug.
  const handleContactChange = (updated: ContactJson) => {
    setOptimisticPipelines(updated.pipelines)
  }

  // Committed write: hand the server's contact up so the drawer refetches against
  // data that already includes the change.
  const handleSaved = (updated: ContactJson) => {
    setOptimisticPipelines(updated.pipelines)
    onContactChange?.(updated)
  }

  // Stage moves and removals share one write. Optimistic, with rollback + toast
  // on failure, mirroring the inbox contact card.
  const persistPipelines = async (next: NonNullable<ContactJson['pipelines']>) => {
    const input = contactPipelineInput(next)
    if (!input) {
      notifyError(`An existing pipeline has no stage. Select its stage before saving.`)
      return
    }

    setOptimisticPipelines(next)
    const result = await contactStore.update(contact.id!, {
      pipelines: input,
    })
    if (result.error) {
      // The `contact` prop is untouched while the mutation is in flight, so dropping
      // the optimistic value restores exactly the pre-mutation state.
      setOptimisticPipelines(undefined)
      notifyError(copy.failedToUpdateList, `pipeline-update-error`)
      return
    }
    handleSaved(
      ((result.data as { updateContact?: ContactJson } | undefined)?.updateContact ?? {
        ...contact,
        pipelines: next,
      }) as ContactJson,
    )
  }

  const changeStage = (pipelineId: string, stage: string) => {
    const current = compact(pipelines)
    return persistPipelines(
      current.some((p) => p.id === pipelineId)
        ? current.map((p) => (p.id === pipelineId ? { ...p, stage } : p))
        : [...current, { id: pipelineId, stage }],
    )
  }

  // Don't show pipelines section if user doesn't have a workspace
  if (!user?.workspaceId) return null

  // With the grouped nav on, a system membership is drawn by its rule row alone. Only what is
  // drawn is counted; a write still sends `pipelines` whole.
  const visiblePipelines = visibleMemberships(compact(pipelines), pipelineStore.hits, { hideSystem: !!groupedNav })
  const systemPipelines = compact(pipelines).filter((p) => isSystemPipelineId(p.id))
  const practiceCards = practice.entries.flatMap((entry) => {
    const catalogEntry = practice.catalog.find((list) => list.id === entry.id)
    return catalogEntry ? [{ entry, catalogEntry }] : []
  })
  const drawnCount = groupedNav ? practice.entries.length : visiblePipelines.length
  const hasPipelines = drawnCount > 0

  return (
    <div className="flex flex-col items-start rounded-md bg-surface p-4">
      <Disclosure defaultOpen>
        {({ open }) => (
          <>
            <Disclosure.Button
              className={twMerge(`flex w-full items-center justify-between`, hasPipelines ? `` : `cursor-default`)}
            >
              <div className="flex w-full items-center gap-2">
                <div className="text-heading-xs text-strong">{copy.NounPlural}</div>
                {hasPipelines && (
                  <>
                    <div className="flex h-5 items-center justify-center rounded border-[0.5px] border-default bg-primary-600 px-1.5">
                      <div className="text-label-xs text-on-color">{drawnCount}</div>
                    </div>
                    <ChevronToggle open={open} className="pr-0" />
                  </>
                )}
              </div>
              <div
                role="button"
                className="size-6 flex items-center justify-center rounded-md border border-default"
                onClick={(evt) => {
                  evt.preventDefault()
                  evt.stopPropagation()
                  showModal(
                    <AddPipeline
                      contact={contact}
                      onContactChange={handleContactChange}
                      onSaved={handleSaved}
                      lists={groupedNav ? practice.addable : undefined}
                      onAddToList={groupedNav ? practice.add : undefined}
                    />,
                    {
                      title: copy.addToList,
                      isFullScreen: false,
                      removePadding: true,
                      overflowHidden: false,
                      useRadix: true,
                    },
                  )
                }}
              >
                <Plus width={16} height={16} className="text-default" />
              </div>
            </Disclosure.Button>

            <Disclosure.Panel className="w-full" unmount={false}>
              <div className="mt-4 flex w-full flex-col gap-2 empty:mt-0">
                {groupedNav && (
                  <RuleListRows
                    contact={{ ...contact, pipelines: systemPipelines }}
                    showHistory
                    onContactChange={(updated) => onContactChange?.({ ...contact, leadStatus: updated.leadStatus })}
                  />
                )}
                {hasPipelines && (
                  <div className="flex w-full flex-col gap-1">
                    {groupedNav
                      ? practiceCards.map(({ entry, catalogEntry }) => (
                          <PipelineCard
                            key={entry.id}
                            pipelineId={entry.id}
                            stageName={entry.stage ?? ``}
                            contactId={contact.id || ``}
                            entry={catalogEntry}
                            onChangeStage={(s) => practice.changeStage(entry.id, s)}
                            onRemove={practice.canRemove(entry.id) ? () => practice.remove(entry.id) : undefined}
                          />
                        ))
                      : visiblePipelines.map((pipeline, index) => (
                          <PipelineCard
                            key={`${pipeline.id}-${pipeline.stage}-${index}`}
                            pipelineId={pipeline.id}
                            stageName={pipeline.stage ?? ``}
                            contactId={contact.id || ``}
                            onChangeStage={(s) => changeStage(pipeline.id, s)}
                            onRemove={() => persistPipelines(compact(pipelines).filter((p) => p.id !== pipeline.id))}
                          />
                        ))}
                  </div>
                )}
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </div>
  )
})
